import { Drawer } from "@picklebet/ui";
import { OTPInput } from "@/features/auth/components/OTPInput/OTPInput";
import type {
  OtpVerificationChannel,
  OtpVerificationPurpose,
  WithOtpFlow,
} from "@/features/auth/utils/types";
import type { OtpStatus } from "../stores/otpStore";
import cx from "classnames";
import pick from "lodash/pick";
import React, { useEffect, useState } from "react";
import { useShallow } from "zustand/shallow";

import { useOtpFlow } from "../hooks/useOtpFlow";
import { useOtpExpiry } from "../hooks/useOtpExpiry";
import { useOtpTelemetry } from "../hooks/useOtpTelemetry";

import { OtpInlineError } from "./OtpInlineError";
import { RetryButton } from "./RetryButton";

import * as styles from "./OtpVerification.module.scss";

type OtpVerificationProps = WithOtpFlow<{
  destination?: string;
  channel: OtpVerificationChannel;
  purpose: OtpVerificationPurpose;
  authToken?: string | null;

  children: (
    RetryButton: React.ReactNode,
    errorMessage: React.ReactNode | null,
    isDrawOpen: boolean,
  ) => React.ReactNode;
  isEnabled?: boolean;
  autoSend?: boolean;
  disabled?: boolean;
  name?: string;
  onSessionExpired?: () => void;
  clearErrors?: (name: string) => void;
  onVerified?: (
    verificationRequestId: string | null,
    deviceBeingVerified: string | null,
  ) => void;
}>;

export const OtpVerification = ({
  flow,
  isEnabled = true,
  destination,
  channel,
  purpose,
  name,
  authToken,
  autoSend = false,
  disabled = false,
  onVerified,
  clearErrors,
  onSessionExpired,
  children,
}: OtpVerificationProps) => {
  const [otpCode, setOtpCode] = useState("");

  const { forceCollapsedError, store } = useOtpFlow(flow);

  const sendOtp = store((s) => s.sendOtp);
  const submitOtp = store((s) => s.submitOtp);
  const autoSendOtp = store((s) => s.autoSendOtp);

  const expire = store((s) => s.expire);
  const clean = store((s) => s.clean);

  const { status, session } = store(
    useShallow((s) => pick(s, ["status", "session"])),
  );

  useEffect(() => {
    autoSendOtp({
      shouldAutoSend: isEnabled && autoSend,
      destination,
      channel,
      purpose,
      authToken,
    });
  }, [isEnabled, autoSend, destination, channel, purpose, authToken]);

  useEffect(() => {
    if (!session) return;
    if (destination !== session.destination) {
      clean();
    }
  }, [destination, session]);

  const handleSessionExpired = () => {
    onSessionExpired?.();
    expire();
  };

  useOtpExpiry(session?.expiresAt, handleSessionExpired);
  useOtpTelemetry(flow);

  const handleVerifyClick = async () => {
    if (clearErrors && name) clearErrors(name);
    await sendOtp(destination, channel, purpose, authToken ?? null);
  };

  const handleSubmit = async () => {
    await submitOtp(otpCode, channel, authToken ?? null, onVerified);
    setOtpCode("");
  };

  const isOpen = Boolean(
    ["codeSent", "retrying"].includes(status) &&
      session?.destination &&
      destination &&
      session?.destination === destination,
  );

  const isSubmitting = store((s) => s.isSubmitting);

  return (
    <>
      {children(
        <RetryButton
          flow={flow}
          isEnabled={isEnabled}
          disabled={disabled}
          loading={isSubmitting}
          onClick={handleVerifyClick}
          variant={isOpen ? "blurred" : "primary"}
          label={isOpen ? "Resend Now" : "Verify Now"}
        />,
        forceCollapsedError && <OtpInlineError flow={flow} />,
        isOpen,
      )}

      <Drawer className={cx(styles.drawer)} isOpen={isOpen}>
        <div className={styles.info}>
          Please enter the verification code sent to your{" "}
          {channel.includes("SMS") ? "mobile" : "email"}.
        </div>

        <OTPInput
          autoFocus
          flow={flow}
          value={otpCode}
          onChange={setOtpCode}
          onComplete={handleSubmit}
          disabled={isSubmitting}
        />
      </Drawer>
    </>
  );
};
